import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { api, call, type Schemas } from '../../api/client';
import { useAction, useChildAssignments, useDashboard, useMembers, usePointEntries, usePoints, useTasks } from '../../api/queries';
import { useHousehold } from '../../api/session';
import { Avatar, Button, Card, EmptyState, ErrorText, Field, Input, Modal, PageHeader, Select, Spinner } from '../../components/ui';
import { timeAgo } from '../../lib/format';
import { ManageRewardsDialog, PointsHistoryRow, PointsOverview, SavingToward, SpendDialog } from './Rewards';
import { TaskList } from '../tasks/TaskCard';

export function ChildrenPage() {
  const dash = useDashboard().data;
  const [adding, setAdding] = useState(false);
  return (
    <>
      <PageHeader title="Children" subtitle="Chores are given to a child only when both parents agree." actions={<Button onClick={() => setAdding(true)}><Plus size={16} /> Add child</Button>} />
      {!dash ? <Spinner /> : dash.counts.children.length === 0 ? (
        <EmptyState title="No children added yet">Children don't need an account or email — just a name.</EmptyState>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {dash.counts.children.map((c) => (
            <Link key={c.memberId} to="/children/$childId" params={{ childId: c.memberId }}>
              <Card className="flex items-center gap-3 hover:bg-[var(--surface-2)]">
                <Avatar name={c.displayName} size={40} />
                <div className="flex-1">
                  <p className="font-medium">{c.displayName}</p>
                  <p className="text-xs text-[var(--muted)]">{c.count} open chore{c.count === 1 ? '' : 's'}</p>
                </div>
                <p className="text-lg font-semibold">⭐ {c.pointsBalance}</p>
              </Card>
            </Link>
          ))}
        </div>
      )}
      {adding ? <AddChildDialog onClose={() => setAdding(false)} /> : null}
    </>
  );
}

function AddChildDialog({ onClose }: { onClose(): void }) {
  const { familyId } = useHousehold();
  const add = useAction(
    (f: FormData) =>
      call(
        api.POST('/api/v1/families/{familyId}/children', {
          params: { path: { familyId } },
          body: { displayName: String(f.get('name')), ...(f.get('label') ? { relationshipLabelCode: f.get('label') as 'CHILD' | 'SON' | 'DAUGHTER' } : {}) },
        }),
      ),
    { success: 'Child added' },
  );
  return (
    <Modal open onOpenChange={onClose} title="Add a child">
      <form className="space-y-4" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); add.mutate(new FormData(e.currentTarget), { onSuccess: onClose }); }}>
        <Field label="Name">{(id) => <Input id={id} name="name" required maxLength={60} autoFocus />}</Field>
        <Field label="Label (optional)">
          {(id) => (
            <Select id={id} name="label" defaultValue="">
              <option value="">None</option><option value="DAUGHTER">Daughter</option><option value="SON">Son</option><option value="CHILD">Child</option>
            </Select>
          )}
        </Field>
        <ErrorText error={add.error} />
        <div className="flex justify-end gap-2"><Button type="button" variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" busy={add.isPending}>Add</Button></div>
      </form>
    </Modal>
  );
}

export function ChildPage() {
  const { childId } = useParams({ from: '/app/children/$childId' });
  const { familyId } = useHousehold();
  const navigate = useNavigate();
  const child = (useMembers().data ?? []).find((m) => m.id === childId);
  const chores = useTasks({ assignee: childId, sort: 'due' });
  const done = useTasks({ assignee: childId, status: 'completed', sort: 'completed' });
  const standing = useChildAssignments(childId).data ?? [];
  const points = usePoints(childId).data;
  const entries = usePointEntries(childId).data?.items ?? [];
  const [bonus, setBonus] = useState(false);
  const [spending, setSpending] = useState<{ reward?: Schemas['RewardResponse'] } | null>(null);
  const [managing, setManaging] = useState(false);
  const archive = useAction(() => call(api.POST('/api/v1/families/{familyId}/children/{childId}/archive', { params: { path: { familyId, childId } } })), { success: 'Profile archived' });

  if (!child) return <Spinner />;
  return (
    <>
      <PageHeader
        title={child.displayName}
        actions={<Button variant="secondary" onClick={() => setBonus(true)}>Bonus or correction…</Button>}
      />
      <PointsOverview points={points} onSpend={() => setSpending({})} />
      {points ? <SavingToward balance={points.balance} onSpend={(reward) => setSpending({ reward })} onManage={() => setManaging(true)} /> : null}
      <h2 className="mb-2 text-sm font-medium text-[var(--muted)]">Chores to do</h2>
      <TaskList tasks={chores.data?.items} empty={<EmptyState title="No open chores">Give a task to {child.displayName} from its page, or set up a recurring chore.</EmptyState>} />

      {standing.length ? (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-medium text-[var(--muted)]">Agreed recurring chores</h2>
          <div className="space-y-2">
            {standing.filter((a) => a.scope === 'SERIES').map((a) => (
              <Link key={a.id} to="/recurring/$seriesId" params={{ seriesId: a.seriesId! }}>
                <Card className="text-sm hover:bg-[var(--surface-2)]">
                  <p className="font-medium">{a.approvedTerms.title}</p>
                  <p className="text-xs text-[var(--muted)]">
                    Approved {timeAgo(a.approvedAt)}{a.approvalBasis === 'SOLE_ADULT' ? ' (single-adult household)' : ' by both parents'}
                    {a.approvedTerms.points ? ` · ⭐ ${a.approvedTerms.points} each` : ''}
                  </p>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section className="mt-6">
        <h2 className="mb-2 text-sm font-medium text-[var(--muted)]">Points history</h2>
        <Card className="divide-y divide-[var(--border)] p-0">
          {entries.length ? entries.map((e) => <PointsHistoryRow key={e.id} entry={e} childId={childId} />) : <p className="p-4 text-sm text-[var(--muted)]">No points yet.</p>}
        </Card>
      </section>

      {done.data?.items.length ? (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-medium text-[var(--muted)]">Recently completed</h2>
          <TaskList tasks={done.data.items.slice(0, 5)} empty={null} />
        </section>
      ) : null}

      <div className="mt-10 border-t border-[var(--border)] pt-4">
        <Button variant="ghost" busy={archive.isPending} onClick={() => { if (confirm(`Archive ${child.displayName}'s profile? Their open chores go back to the House Pool. History and points are kept.`)) archive.mutate(undefined, { onSuccess: () => void navigate({ to: '/children' }) }); }}>
          Archive profile
        </Button>
      </div>
      {bonus ? <PointsDialog childId={childId} name={child.displayName} onClose={() => setBonus(false)} /> : null}
      {spending && points ? <SpendDialog childId={childId} name={child.displayName} balance={points.balance} initial={spending.reward} onClose={() => setSpending(null)} /> : null}
      {managing ? <ManageRewardsDialog onClose={() => setManaging(false)} /> : null}
    </>
  );
}

function PointsDialog({ childId, name, onClose }: { childId: string; name: string; onClose(): void }) {
  const { familyId } = useHousehold();
  const [kind, setKind] = useState<'bonus' | 'correction'>('bonus');
  const adjust = useAction(
    (f: FormData) =>
      call(
        api.POST('/api/v1/families/{familyId}/children/{childId}/points-adjustments', {
          params: { path: { familyId, childId }, header: { 'Idempotency-Key': crypto.randomUUID() } },
          body: { points: (kind === 'bonus' ? 1 : -1) * Math.abs(Number(f.get('points'))), reason: String(f.get('reason')) },
        }),
      ),
    { success: 'Points updated' },
  );
  return (
    <Modal open onOpenChange={onClose} title={`${name}'s points`}>
      <form className="space-y-4" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); adjust.mutate(new FormData(e.currentTarget), { onSuccess: onClose }); }}>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant={kind === 'bonus' ? 'primary' : 'secondary'} onClick={() => setKind('bonus')}>Give a bonus</Button>
          <Button type="button" size="sm" variant={kind === 'correction' ? 'primary' : 'secondary'} onClick={() => setKind('correction')}>Correct a mistake</Button>
        </div>
        <Field label="Points">{(id) => <Input id={id} name="points" type="number" min={1} max={10000} required />}</Field>
        <Field label="Reason" hint="Your partner will see this.">{(id) => <Input id={id} name="reason" required maxLength={300} placeholder={kind === 'bonus' ? 'Helped without being asked' : 'Counted twice by mistake'} />}</Field>
        <ErrorText error={adjust.error} />
        <div className="flex justify-end gap-2"><Button type="button" variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" busy={adjust.isPending}>Save</Button></div>
      </form>
    </Modal>
  );
}
